import { useState, useEffect, useCallback } from "react";
import { Card, Spin, Table, Button, Modal, Select } from "@/frontend/components/common";
import { FaUsers, FaTrash, FaStar, FaPlus } from "react-icons/fa";
import { tournamentTeamPlayerService, tournamentParticipantService, playerService } from "@/frontend/services";
import type { TournamentTeamPlayerResponseDTO } from "@/shared/dto";
import type { PlayerResponseDTO } from "@/frontend/services/playerService";
import type { ColumnsType } from "antd/es/table";
import type { SeasonInfo, TournamentStageInfo } from "../types";

interface TournamentParticipantWithTeam {
  id: string;
  teamId: string | null;
  team?: {
    id: string;
    name: string;
    logo?: string | null;
  } | null;
}

interface TeamPlayersTabProps {
  tournamentId: string;
  currentSeason?: SeasonInfo;
  tournamentStages: TournamentStageInfo[];
}

export function TeamPlayersTab({
  tournamentId,
  currentSeason,
  tournamentStages,
}: TeamPlayersTabProps) {
  const [teams, setTeams] = useState<TournamentParticipantWithTeam[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [teamPlayers, setTeamPlayers] = useState<TournamentTeamPlayerResponseDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingPlayers, setLoadingPlayers] = useState(false);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [allPlayers, setAllPlayers] = useState<PlayerResponseDTO[]>([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [isCaptain, setIsCaptain] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [mode, setMode] = useState<"select" | "create">("select");
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newNickname, setNewNickname] = useState("");
  const [loadingCreate, setLoadingCreate] = useState(false);

  const fetchTeams = useCallback(async () => {
    if (!currentSeason?.id) return;

    setLoading(true);
    try {
      const response = await tournamentParticipantService.getPaginate({
        tournamentId,
        seasonId: currentSeason.id,
        page: 1,
        pageSize: 100,
      });

      if (response.success && response.data) {
        const participantsWithTeam = (response.data.data || []).filter(
          (p: TournamentParticipantWithTeam) => p.teamId !== null
        );
        setTeams(participantsWithTeam);
      }
    } catch (error) {
      console.error("Error al cargar equipos:", error);
    } finally {
      setLoading(false);
    }
  }, [tournamentId, currentSeason?.id]);

  const fetchTeamPlayers = useCallback(async () => {
    if (!selectedTeamId || !currentSeason?.id) {
      setTeamPlayers([]);
      return;
    }

    setLoadingPlayers(true);
    try {
      const response = await tournamentTeamPlayerService.getPaginate({
        tournamentId,
        seasonId: currentSeason.id,
        teamId: selectedTeamId,
        page: 1,
        pageSize: 100,
      });

      if (response.success && response.data) {
        setTeamPlayers(response.data.data || []);
      }
    } catch (error) {
      console.error("Error al cargar jugadores del equipo:", error);
    } finally {
      setLoadingPlayers(false);
    }
  }, [selectedTeamId, tournamentId, currentSeason?.id]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  useEffect(() => {
    fetchTeamPlayers();
  }, [fetchTeamPlayers]);

  const handleOpenAddModal = async () => {
    setAddModalOpen(true);
    setSelectedPlayerId(null);
    setIsCaptain(false);
    setMode("select");
    setNewFirstName("");
    setNewLastName("");
    setNewNickname("");

    try {
      const players = await playerService.getAll();
      setAllPlayers(players);
    } catch (error) {
      console.error("Error al cargar jugadores:", error);
    }
  };

  const handleCreateAndAdd = async () => {
    if (!newFirstName.trim() || !newLastName.trim() || !selectedTeamId || !currentSeason?.id) return;

    setLoadingCreate(true);
    try {
      const createRes = await playerService.create({
        firstName: newFirstName.trim(),
        lastName: newLastName.trim(),
        nickname: newNickname.trim() || undefined,
      });

      if (!createRes.success || !createRes.data) {
        Modal.error({ title: "Error", content: "No se pudo crear el jugador" });
        return;
      }

      const assignRes = await tournamentTeamPlayerService.create({
        tournamentId,
        seasonId: currentSeason.id,
        teamId: selectedTeamId,
        playerId: createRes.data.id,
        isCaptain,
      });

      if (assignRes.success) {
        setAddModalOpen(false);
        fetchTeamPlayers();
      } else {
        Modal.error({ title: "Error", content: (assignRes as any).error ?? "No se pudo asignar el jugador" });
      }
    } catch (error) {
      Modal.error({ title: "Error", content: "Error al crear y asignar el jugador" });
    } finally {
      setLoadingCreate(false);
    }
  };

  const handleAddPlayer = async () => {
    if (!selectedPlayerId || !selectedTeamId || !currentSeason?.id) return;

    setSubmitting(true);
    try {
      const response = await tournamentTeamPlayerService.create({
        tournamentId,
        seasonId: currentSeason.id,
        teamId: selectedTeamId,
        playerId: selectedPlayerId,
        isCaptain,
      });

      if (response.success) {
        setAddModalOpen(false);
        fetchTeamPlayers();
      } else {
        Modal.error({
          title: "Error",
          content: (response as any).error ?? "No se pudo agregar el jugador",
        });
      }
    } catch (error) {
      Modal.error({
        title: "Error",
        content: "No se pudo agregar el jugador",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePlayer = useCallback(
    (player: TournamentTeamPlayerResponseDTO) => {
      Modal.confirm({
        title: "Eliminar jugador del equipo",
        content: `¿Estás seguro de eliminar ${player.player?.firstName ?? ""} ${player.player?.lastName ?? ""} del equipo?`,
        okText: "Eliminar",
        cancelText: "Cancelar",
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            const response = await tournamentTeamPlayerService.delete(player.id);
            if (response.success) {
              fetchTeamPlayers();
            } else {
              Modal.error({
                title: "Error",
                content: (response as any).error ?? "No se pudo eliminar el jugador",
              });
            }
          } catch (error) {
            Modal.error({
              title: "Error",
              content: "No se pudo eliminar el jugador",
            });
          }
        },
      });
    },
    [fetchTeamPlayers]
  );

  const handleMakeCaptain = useCallback(
    async (player: TournamentTeamPlayerResponseDTO) => {
      try {
        const response = await tournamentTeamPlayerService.update(player.id, {
          isCaptain: true,
        });

        if (response.success) {
          fetchTeamPlayers();
        } else {
          Modal.error({
            title: "Error",
            content: (response as any).error ?? "No se pudo designar al capitán",
          });
        }
      } catch (error) {
        Modal.error({
          title: "Error",
          content: "No se pudo designar al capitán",
        });
      }
    },
    [fetchTeamPlayers]
  );

  const selectedTeam = teams.find((t) => t.teamId === selectedTeamId);

  const playerColumns: ColumnsType<TournamentTeamPlayerResponseDTO> = [
    {
      title: "Nombre",
      key: "name",
      render: (_, record) => (
        <div className="flex items-center gap-2">
          {record.player?.imageUrl && (
            <img
              src={record.player.imageUrl}
              alt=""
              className="w-8 h-8 rounded-full object-cover"
            />
          )}
          <div>
            <div className="font-medium">
              {record.player?.firstName ?? ""} {record.player?.lastName ?? ""}
            </div>
            {record.player?.nickname && (
              <div className="text-xs text-gray-500">
                "{record.player.nickname}"
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Apodo",
      dataIndex: "player",
      key: "nickname",
      width: 150,
      render: (_, record) => record.player?.nickname ?? "-",
    },
    {
      title: "Capitán",
      key: "captain",
      width: 120,
      align: "center",
      render: (_, record) =>
        record.isCaptain ? (
          <span className="inline-flex items-center gap-1 text-yellow-600 font-medium">
            <FaStar className="text-yellow-500" />
            Capitán
          </span>
        ) : (
          <Button
            type="link"
            size="small"
            onClick={() => handleMakeCaptain(record)}
          >
            Hacer capitán
          </Button>
        ),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Button
          type="link"
          danger
          icon={<FaTrash />}
          onClick={() => handleDeletePlayer(record)}
          title="Eliminar jugador"
        />
      ),
    },
  ];

  if (!currentSeason) {
    return (
      <Card className="shadow-sm">
        <div className="text-center py-12">
          <FaUsers className="mx-auto text-gray-300 text-5xl mb-4" />
          <p className="text-gray-500">
            No hay una temporada actual seleccionada
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="py-4">
      <div className="flex gap-4">
        <Card className="shadow-sm w-64 flex-shrink-0">
          <h3 className="font-semibold text-gray-700 mb-3">Equipos</h3>
          {loading ? (
            <div className="flex justify-center py-8">
              <Spin size="small" />
            </div>
          ) : teams.length > 0 ? (
            <div className="space-y-1">
              {teams.map((participant) => (
                <button
                  key={participant.id}
                  onClick={() => setSelectedTeamId(participant.teamId)}
                  className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                    selectedTeamId === participant.teamId
                      ? "bg-blue-100 text-blue-700 font-medium"
                      : "hover:bg-gray-100 text-gray-700"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {participant.team?.logo && (
                      <img
                        src={participant.team.logo}
                        alt=""
                        className="w-5 h-5 object-contain rounded"
                      />
                    )}
                    <span className="truncate">{participant.team?.name ?? "Sin nombre"}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              No hay equipos participantes
            </div>
          )}
        </Card>

        <Card className="shadow-sm flex-1">
          {selectedTeamId ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-700">
                  Jugadores de {selectedTeam?.team?.name ?? "Equipo"}
                </h3>
                <Button
                  type="primary"
                  icon={<FaPlus />}
                  onClick={handleOpenAddModal}
                >
                  Agregar Jugador
                </Button>
              </div>
              {loadingPlayers ? (
                <div className="flex justify-center items-center py-12">
                  <Spin size="large" tip="Cargando jugadores..." />
                </div>
              ) : teamPlayers.length > 0 ? (
                <Table
                  columns={playerColumns}
                  dataSource={teamPlayers}
                  rowKey="id"
                  size="middle"
                  pagination={false}
                />
              ) : (
                <div className="text-center py-12">
                  <FaUsers className="mx-auto text-gray-300 text-5xl mb-4" />
                  <p className="text-gray-500">
                    No hay jugadores asignados a este equipo
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <FaUsers className="mx-auto text-gray-300 text-5xl mb-4" />
              <p className="text-gray-500">
                Selecciona un equipo para ver sus jugadores
              </p>
            </div>
          )}
        </Card>
      </div>

      <Modal
        title="Agregar Jugador al Equipo"
        open={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        onOk={mode === "select" ? handleAddPlayer : handleCreateAndAdd}
        okText={mode === "select" ? "Agregar" : "Crear y Agregar"}
        cancelText="Cancelar"
        confirmLoading={submitting || loadingCreate}
        okButtonProps={{
          disabled: mode === "select" ? !selectedPlayerId : !newFirstName.trim() || !newLastName.trim(),
        }}
      >
        <div className="space-y-4 py-2">
          <div className="flex gap-2 mb-4">
            <Button
              type={mode === "select" ? "primary" : "default"}
              onClick={() => setMode("select")}
              className="flex-1"
            >
              Seleccionar existente
            </Button>
            <Button
              type={mode === "create" ? "primary" : "default"}
              onClick={() => setMode("create")}
              className="flex-1"
            >
              Crear nuevo
            </Button>
          </div>

          {mode === "select" ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Jugador
              </label>
              <Select
                showSearch
                placeholder="Buscar jugador..."
                optionFilterProp="label"
                style={{ width: "100%" }}
                value={selectedPlayerId}
                onChange={(value) => setSelectedPlayerId(value)}
                options={allPlayers.map((p) => ({
                  value: p.id,
                  label: `${p.firstName} ${p.lastName}${p.nickname ? ` (${p.nickname})` : ""}`,
                }))}
                loading={allPlayers.length === 0}
              />
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={newFirstName}
                  onChange={(e) => setNewFirstName(e.target.value)}
                  placeholder="Ej: Lionel"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Apellido *
                </label>
                <input
                  type="text"
                  value={newLastName}
                  onChange={(e) => setNewLastName(e.target.value)}
                  placeholder="Ej: Messi"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Apodo
                </label>
                <input
                  type="text"
                  value={newNickname}
                  onChange={(e) => setNewNickname(e.target.value)}
                  placeholder="Ej: Leo"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isCaptain"
              checked={isCaptain}
              onChange={(e) => setIsCaptain(e.target.checked)}
              className="rounded border-gray-300"
            />
            <label htmlFor="isCaptain" className="text-sm text-gray-700">
              Designar como capitán
            </label>
          </div>
        </div>
      </Modal>
    </div>
  );
}
